import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { MdBolt, MdGroup, MdTrackChanges, MdAccessTime, MdSecurity, MdCloudSync, MdSearch, MdKeyboardCommandKey, MdBarChart, MdRepeat, MdCheckCircle, MdEdit, MdFilterList, MdDashboard, MdPause, MdPlayArrow, MdCheck, MdCalendarToday, MdArrowForward, MdPerson, MdDomain, MdAdd, MdRemove } from 'react-icons/md';
import { useState, useEffect } from 'react';
import HeroSection from '../components/HeroSection';

export default function LandingPage() {
  const [activePersona, setActivePersona] = useState('individuals');
  const [openFaq, setOpenFaq] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [timerSeconds, setTimerSeconds] = useState(1500);
  const [progressValue, setProgressValue] = useState(2);
  const [isRepeating, setIsRepeating] = useState(false);
  const [repeatType, setRepeatType] = useState('weekly');
  const [happinessScore, setHappinessScore] = useState(7);

  // Timer countdown effect
  useEffect(() => {
    let interval;
    if (isTimerRunning && timerSeconds > 0) {
      interval = setInterval(() => {
        setTimerSeconds(prev => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isTimerRunning, timerSeconds]);

  const demoTasks = [
    { id: 1, title: 'Redesign Dashboard', highlighted: 'Dashboard' },
    { id: 2, title: 'Coffee with Sarah', highlighted: 'Coffee' },
    { id: 3, title: 'Espresso machine manual', highlighted: 'Espresso' },
  ];

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen bg-white text-[#202124] google-font">
      <Helmet>
        <title>Snappy — Your Daily Tasks Organized Effortlessly</title>
        <meta name="description" content="Snappy helps you manage daily tasks, assign teammates, and track progress — all in a simple, fast, and visual workspace." />
        <link rel="canonical" href="https://snappy-todo.com" />
        <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap" rel="stylesheet" />
      </Helmet>

      {/* New Hero Section with Navbar */}
      <HeroSection />

      {/* Trusted By Marquee (Google Style) */}
      <section className="py-12 border-b border-gray-100 bg-white overflow-hidden">
        <div className="max-w-[1200px] mx-auto px-6">
          <p className="text-center text-[13px] font-bold text-gray-400 uppercase tracking-widest mb-8">Trusted by innovative teams worldwide</p>
          <div className="flex justify-center flex-wrap gap-12 md:gap-24 items-center opacity-50 grayscale hover:grayscale-0 transition-all duration-500">
            <div className="text-xl font-bold flex items-center gap-2 text-gray-600"><MdCloudSync size={24} /> Acme Corp</div>
            <div className="text-xl font-bold flex items-center gap-2 text-gray-600"><MdDashboard size={24} /> Globex</div>
            <div className="text-xl font-bold flex items-center gap-2 text-gray-600"><MdSecurity size={24} /> Soylent</div>
            <div className="text-xl font-bold flex items-center gap-2 text-gray-600 hidden md:flex"><MdBarChart size={24} /> Initech</div>
            <div className="text-xl font-bold flex items-center gap-2 text-gray-600 hidden lg:flex"><MdCheckCircle size={24} /> Massive Dynamic</div>
          </div>
        </div>
      </section>

      {/* Interactive Features Showcase */}
      <section className="py-32 px-6 bg-[#f8f9fa] relative overflow-hidden">
        {/* Smooth gradient overlay from top */}
        <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-white to-transparent pointer-events-none"></div>
        
        <div className="max-w-[1200px] mx-auto relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-[40px] md:text-[56px] font-bold mb-6 text-[#202124] tracking-tight">
              Experience the <span className="text-[#1a73e8]">Power</span>
            </h2>
            <p className="text-[20px] text-[#5f6368]">
              Interactive features designed to boost your productivity
            </p>
          </div>

          {/* Persona Tabs */}
          <div className="flex flex-wrap justify-center gap-4 mb-16">
            {[
              { id: 'individuals', icon: MdPerson, label: 'For Individuals' },
              { id: 'teams', icon: MdGroup, label: 'For Teams' },
              { id: 'enterprise', icon: MdDomain, label: 'For Enterprise' },
            ].map((persona) => (
              <button
                key={persona.id}
                onClick={() => setActivePersona(persona.id)}
                className={`px-8 py-4 rounded-full font-medium transition-all flex items-center gap-3 text-[16px] ${
                  activePersona === persona.id
                    ? 'bg-[#e8f0fe] text-[#1a73e8] shadow-sm ring-1 ring-[#1a73e8]/20'
                    : 'bg-white text-[#5f6368] hover:bg-gray-50 border border-gray-200 hover:shadow-sm'
                }`}
              >
                <persona.icon size={22} />
                <span>{persona.label}</span>
              </button>
            ))}
          </div>

          {/* Interactive Demo Area */}
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: Interactive Component */}
            <motion.div
              key={activePersona}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-white rounded-[2.5rem] p-8 md:p-10 border border-gray-100 flex flex-col gap-10 hover:shadow-2xl transition-shadow duration-500"
              style={{ boxShadow: '0 24px 64px -12px rgba(0,0,0,0.08), 0 12px 24px -8px rgba(0,0,0,0.04)' }}
            >
              <AnimatePresence mode="popLayout">
                {/* Smart Search Feature */}
                {activePersona === 'teams' && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="space-y-4"
                  >
                    <div className="flex items-center gap-2 mb-4">
                      <MdSearch className="text-gray-900 text-2xl" />
                      <h3 className="text-xl font-bold text-gray-900">Smart Search</h3>
                    </div>
                    <div className="relative">
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Try: tag:work, due:today, @john..."
                        className="w-full px-5 py-4 bg-[#f1f3f4] border-2 border-transparent rounded-[1.5rem] focus:bg-white focus:border-[#1a73e8] focus:outline-none text-[#202124] placeholder-[#5f6368] transition-all"
                      />
                      <MdSearch className="absolute right-4 top-4 text-gray-400" />
                    </div>
                    
                    {searchQuery && (
                      <div className="mt-4 max-h-64 overflow-y-auto">
                        <p className="text-sm text-gray-500 font-medium mb-2">FOUND 3 RESULTS</p>
                        <div className="space-y-2">
                          {demoTasks
                            .filter(task => 
                              task.title.toLowerCase().includes(searchQuery.toLowerCase())
                            )
                            .map((task) => (
                              <div key={task.id} className="p-3 bg-gray-100 rounded-lg hover:bg-gray-200 transition-all cursor-pointer">
                                <p className="text-sm text-gray-500">Tasks / Projects</p>
                                <p className="text-gray-900">
                                  The{' '}
                                  <span className="bg-green-500/30 text-green-700 px-1 rounded">
                                    {task.highlighted}
                                  </span>{' '}
                                  {task.title.replace(task.highlighted, '').trim()}
                                </p>
                              </div>
                            ))}
                        </div>
                      </div>
                    )}

                    <div className="mt-6 p-5 bg-[#e8f0fe] border border-[#d2e3fc] rounded-[1.5rem]">
                      <p className="text-sm text-[#1a73e8] font-medium mb-3">Search Syntax:</p>
                      <div className="space-y-2 text-sm text-[#5f6368]">
                        <p><code className="bg-white px-2 py-0.5 rounded-md text-[#202124] shadow-sm">tag:work</code> - Filter by tags</p>
                        <p><code className="bg-white px-2 py-0.5 rounded-md text-[#202124] shadow-sm">due:today</code> - Due date filter</p>
                        <p><code className="bg-white px-2 py-0.5 rounded-md text-[#202124] shadow-sm">@user</code> - Find mentions</p>
                        <p><code className="bg-white px-2 py-0.5 rounded-md text-[#202124] shadow-sm">priority:high</code> - Priority filter</p>
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Command Palette Feature */}
                {activePersona === 'teams' && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="space-y-4"
                  >
                    <div className="flex items-center gap-2 mb-4">
                      <MdKeyboardCommandKey className="text-gray-900 text-2xl" />
                      <h3 className="text-xl font-bold text-gray-900">Command Palette</h3>
                    </div>
                    
                    <div className="bg-gray-50 border border-gray-200 text-gray-900 rounded-lg p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <MdSearch className="text-gray-400" />
                        <input
                          type="text"
                          placeholder="Type a command..."
                          className="flex-1 bg-transparent border-none outline-none text-gray-900 placeholder-gray-400"
                        />
                        <div className="px-2 py-1 bg-gray-200 rounded text-xs text-gray-600">⌘K</div>
                      </div>
                      
                      <div className="space-y-2">
                        {[
                          { icon: MdEdit, label: 'New Task', shortcut: '⌘⇧T' },
                          { icon: MdTrackChanges, label: 'Focus Mode', shortcut: '' },
                          { icon: MdBarChart, label: 'Activity Timeline', shortcut: '' },
                          { icon: MdFilterList, label: 'Advanced Filters', shortcut: '⌘⇧F' },
                          { icon: MdDashboard, label: 'Toggle Kanban View', shortcut: '⌘⇧K' },
                        ].map((cmd, idx) => (
                          <div
                            key={idx}
                            className="flex items-center justify-between p-3 rounded hover:bg-gray-100 transition-all cursor-pointer"
                          >
                            <div className="flex items-center gap-3">
                              <cmd.icon className="text-gray-400" />
                              <span>{cmd.label}</span>
                            </div>
                            {cmd.shortcut && (
                              <span className="text-xs text-gray-400">{cmd.shortcut}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="p-4 bg-amber-50 rounded-lg">
                      <p className="text-sm text-amber-900 font-medium mb-2">Keyboard Shortcuts:</p>
                      <div className="grid grid-cols-2 gap-2 text-xs text-amber-800">
                        <p>⌘K - Open palette</p>
                        <p>⌘⇧T - Quick add</p>
                        <p>⌘F - Search</p>
                        <p>Esc - Close</p>
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Focus Timer Feature */}
                {activePersona === 'individuals' && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="space-y-6"
                  >
                    <div className="flex items-center gap-2 mb-4">
                      <MdTrackChanges className="text-gray-900 text-2xl" />
                      <h3 className="text-xl font-bold text-gray-900">Focus Timer</h3>
                    </div>

                    <div className="text-center">
                      <div className="text-6xl font-bold text-gray-900 mb-6">
                        {formatTime(timerSeconds)}
                      </div>
                      <div className="flex justify-center gap-3 mb-6">
                        <button
                          onClick={() => setIsTimerRunning(!isTimerRunning)}
                          className="px-8 py-3.5 bg-[#1a73e8] text-white rounded-full hover:bg-[#1557b0] transition-all flex items-center gap-2 font-medium"
                        >
                          {isTimerRunning ? (
                            <>
                              <MdPause /> Pause
                            </>
                          ) : (
                            <>
                              <MdPlayArrow /> Start
                            </>
                          )}
                        </button>
                        <button
                          onClick={() => setTimerSeconds(1500)}
                          className="px-6 py-3 border-2 border-gray-200 text-gray-700 rounded-lg hover:bg-gray-100 transition-all"
                        >
                          Reset
                        </button>
                      </div>

                      <div className="flex justify-center gap-2 mb-6">
                        {[
                          { label: '25 min', value: 1500 },
                          { label: '45 min', value: 2700 },
                          { label: '60 min', value: 3600 },
                        ].map((preset) => (
                          <button
                            key={preset.value}
                            onClick={() => setTimerSeconds(preset.value)}
                            className={`px-5 py-2.5 rounded-full text-[15px] font-medium transition-all ${
                              timerSeconds === preset.value
                                ? 'bg-[#e8f0fe] text-[#1a73e8]'
                                : 'bg-[#f1f3f4] text-[#5f6368] hover:bg-gray-200'
                            }`}
                          >
                            {preset.label}
                          </button>
                        ))}
                      </div>

                      <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="flex items-center justify-between text-sm mb-2">
                          <span className="text-gray-400">Current Task</span>
                          <span className="font-medium text-gray-900">Redesign Dashboard</span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-400">Completed Sessions</span>
                          <span className="font-medium text-gray-900">3 sessions</span>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Progress Tracking Feature */}
                {activePersona === 'enterprise' && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="space-y-6"
                  >
                    <div className="flex items-center gap-2 mb-4">
                      <MdBarChart className="text-gray-900 text-2xl" />
                      <h3 className="text-xl font-bold text-gray-900">Progress Tracking</h3>
                    </div>

                    <div className="bg-white text-gray-900 rounded-xl p-6 border border-gray-200">
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-sm opacity-80">COMPLETION RATE</span>
                        <span className="text-2xl font-bold">{progressValue}/4 Completed</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
                        <div
                          className="bg-gray-900 h-3 rounded-full transition-all duration-500"
                          style={{ width: `${(progressValue / 4) * 100}%` }}
                        />
                      </div>
                      <div className="flex justify-between text-xs opacity-70">
                        <span>Started</span>
                        <span>{Math.round((progressValue / 4) * 100)}%</span>
                        <span>Complete</span>
                      </div>
                    </div>

                    <div className="space-y-2">
                      {[
                        { label: 'How it all started', done: true },
                        { label: 'Our values and approach', done: true },
                        { label: 'Working together', done: false },
                        { label: 'Company culture', done: false },
                      ].map((item, idx) => (
                        <div
                          key={idx}
                          onClick={() => {
                            if (idx < 2) setProgressValue(Math.max(0, progressValue - 1));
                            else setProgressValue(Math.min(4, progressValue + 1));
                          }}
                          className="flex items-center gap-3 p-3 bg-gray-100 rounded-lg hover:bg-gray-200 transition-all cursor-pointer border border-gray-200"
                        >
                          <div
                            className={`w-6 h-6 rounded-full flex items-center justify-center ${
                              item.done
                                ? 'bg-green-500 text-white'
                                : 'bg-gray-300 text-gray-400'
                            }`}
                          >
                            {item.done && <MdCheck size={14} />}
                          </div>
                          <span
                            className={`flex-1 ${
                              item.done ? 'text-white' : 'text-gray-400'
                            }`}
                          >
                            {item.label}
                          </span>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}

                {/* Recurring Tasks Feature */}
                {activePersona === 'enterprise' && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="space-y-6"
                  >
                    <div className="flex items-center gap-2 mb-4">
                      <MdRepeat className="text-gray-900 text-2xl" />
                      <h3 className="text-xl font-bold text-gray-900">Recurring Tasks</h3>
                    </div>

                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-4 bg-gray-100 rounded-lg border border-gray-200">
                        <span className="font-medium text-gray-900">Is repeating</span>
                        <button
                          onClick={() => setIsRepeating(!isRepeating)}
                          className={`relative w-14 h-7 rounded-full transition-all ${
                            isRepeating ? 'bg-green-500' : 'bg-gray-300'
                          }`}
                        >
                          <div
                            className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform ${
                              isRepeating ? 'transform translate-x-7' : ''
                            }`}
                          />
                        </button>
                      </div>

                      {isRepeating && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className="space-y-4"
                        >
                          <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                              Repeat
                            </label>
                            <select
                              value={repeatType}
                              onChange={(e) => setRepeatType(e.target.value)}
                              className="w-full px-5 py-4 bg-[#f1f3f4] border-2 border-transparent rounded-[1.5rem] focus:bg-white focus:border-[#1a73e8] focus:outline-none text-[#202124] transition-all"
                            >
                              <option value="daily">Daily</option>
                              <option value="weekly">Weekly</option>
                              <option value="monthly">Monthly</option>
                            </select>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-300 mb-3">
                              Repeat on
                            </label>
                            <div className="grid grid-cols-7 gap-2">
                              {['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map((day, idx) => (
                                <button
                                  key={day}
                                  className={`p-2 text-xs font-medium rounded-full transition-all ${
                                    idx < 5
                                      ? 'bg-[#1a73e8] text-white shadow-sm'
                                      : 'bg-[#f1f3f4] text-[#5f6368] hover:bg-gray-200'
                                  }`}
                                >
                                  {day}
                                </button>
                              ))}
                            </div>
                          </div>

                          <div className="flex items-center gap-2 p-4 bg-[#e8f0fe] rounded-[1.5rem] border border-[#d2e3fc]">
                            <MdCalendarToday className="text-[#1a73e8]" />
                            <span className="text-sm text-[#1a73e8] font-medium">
                              Task will repeat every {repeatType}
                            </span>
                          </div>
                        </motion.div>
                      )}
                    </div>
                  </motion.div>
                )}

                {/* Mood Tracker Feature */}
                {activePersona === 'individuals' && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="space-y-6"
                  >
                    <div className="flex items-center gap-2 mb-4">
                      <MdCheckCircle className="text-gray-900 text-2xl" />
                      <h3 className="text-xl font-bold text-gray-900">Mood & Energy Tracking</h3>
                    </div>

                    <div className="text-center">
                      <p className="text-lg font-medium text-gray-900 mb-4">
                        How happy are you at work?
                      </p>
                      
                      <div className="flex justify-center items-center gap-2 mb-6">
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                          <button
                            key={num}
                            onClick={() => setHappinessScore(num)}
                            className={`w-10 h-10 rounded-full font-bold transition-all ${
                              num === happinessScore
                                ? 'bg-[#1a73e8] text-white scale-110 shadow-[0_4px_14px_rgba(26,115,232,0.3)]'
                                : num < happinessScore
                                ? 'bg-[#f1f3f4] text-[#5f6368]'
                                : 'bg-[#f8f9fa] text-gray-300 hover:bg-gray-100'
                            }`}
                          >
                            {num}
                          </button>
                        ))}
                      </div>

                      <div className="relative w-full h-2 bg-gray-700 rounded-full mb-4">
                        <div
                          className="absolute top-0 left-0 h-2 bg-gradient-to-r from-[#ea4335] via-[#fbbc04] to-[#34a853] rounded-full transition-all duration-300"
                          style={{ width: `${(happinessScore / 10) * 100}%` }}
                        />
                      </div>

                      <p className="text-sm text-gray-300">
                        {happinessScore <= 3 && "Let's turn that around! 💪"}
                        {happinessScore > 3 && happinessScore <= 7 && "Good! Room for improvement 👍"}
                        {happinessScore > 7 && "Excellent! Keep it up! 🎉"}
                      </p>
                    </div>

                    <div className="grid grid-cols-3 gap-3 pt-4">
                      <div className="p-4 bg-green-500/20 rounded-lg text-center border border-green-500/30">
                        <div className="text-2xl font-bold text-green-400">40</div>
                        <div className="text-xs text-green-300">Promoters</div>
                      </div>
                      <div className="p-4 bg-gray-100 rounded-lg text-center border border-gray-200">
                        <div className="text-2xl font-bold text-gray-300">23</div>
                        <div className="text-xs text-gray-400">Passives</div>
                      </div>
                      <div className="p-4 bg-red-500/20 rounded-lg text-center border border-red-500/30">
                        <div className="text-2xl font-bold text-red-400">17</div>
                        <div className="text-xs text-red-300">Detractors</div>
                      </div>
                    </div>

                    <div className="p-4 bg-green-500/20 rounded-lg flex items-center justify-between border border-green-500/30">
                      <span className="text-sm font-medium text-green-300">eNPS Score</span>
                      <span className="text-2xl font-bold text-green-400">+28.8</span>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>

            {/* Right: Feature Description */}
            <motion.div
              key={`desc-${activePersona}`}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4 }}
              className="space-y-12"
            >
              {activePersona === 'teams' && (
                <div>
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">
                    Advanced Search Syntax
                  </h3>
                  <p className="text-lg text-gray-500 mb-6">
                    Find anything instantly with powerful search operators. Filter by tags, mentions, due dates, priority, and more.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <MdCheck className="text-green-400" size={14} />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">Natural Language</h4>
                        <p className="text-gray-500 text-sm">Search using plain English or advanced syntax</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <MdCheck className="text-green-400" size={14} />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">Instant Results</h4>
                        <p className="text-gray-500 text-sm">Results appear as you type with highlighted matches</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <MdCheck className="text-green-400" size={14} />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">Smart Filters</h4>
                        <p className="text-gray-500 text-sm">Combine multiple filters for precise results</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activePersona === 'teams' && (
                <div>
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">
                    Keyboard-First Workflow
                  </h3>
                  <p className="text-lg text-gray-500 mb-6">
                    Access any feature instantly with the command palette. Navigate your entire workspace without touching your mouse.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <MdCheck className="text-green-400" size={14} />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">Lightning Fast</h4>
                        <p className="text-gray-500 text-sm">Execute actions in milliseconds with keyboard shortcuts</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <MdCheck className="text-green-400" size={14} />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">Customizable</h4>
                        <p className="text-gray-500 text-sm">Create your own shortcuts and commands</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <MdCheck className="text-green-400" size={14} />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">Learn As You Go</h4>
                        <p className="text-gray-500 text-sm">Shortcuts displayed inline for easy learning</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activePersona === 'individuals' && (
                <div>
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">
                    Pomodoro Focus Timer
                  </h3>
                  <p className="text-lg text-gray-500 mb-6">
                    Stay focused with customizable work sessions. Track your focus time and build productive habits.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <MdCheck className="text-green-400" size={14} />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">Distraction-Free</h4>
                        <p className="text-gray-500 text-sm">Full-screen mode with ambient sounds</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <MdCheck className="text-green-400" size={14} />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">Auto-Logging</h4>
                        <p className="text-gray-500 text-sm">Sessions automatically tracked to your tasks</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <MdCheck className="text-green-400" size={14} />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">Smart Breaks</h4>
                        <p className="text-gray-500 text-sm">Automatic break reminders to prevent burnout</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activePersona === 'enterprise' && (
                <div>
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">
                    Visual Progress Tracking
                  </h3>
                  <p className="text-lg text-gray-500 mb-6">
                    See your accomplishments with beautiful progress indicators. Stay motivated with visual feedback.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <MdCheck className="text-green-400" size={14} />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">Real-Time Updates</h4>
                        <p className="text-gray-500 text-sm">Watch your progress bars fill as you complete tasks</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <MdCheck className="text-green-400" size={14} />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">Multiple Views</h4>
                        <p className="text-gray-500 text-sm">Track progress by project, list, or time period</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <MdCheck className="text-green-400" size={14} />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">Milestone Celebrations</h4>
                        <p className="text-gray-500 text-sm">Get recognized when you hit important milestones</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activePersona === 'enterprise' && (
                <div>
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">
                    Recurring Tasks Made Easy
                  </h3>
                  <p className="text-lg text-gray-500 mb-6">
                    Set up tasks that repeat automatically. Perfect for habits, routines, and recurring responsibilities.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <MdCheck className="text-green-400" size={14} />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">Flexible Scheduling</h4>
                        <p className="text-gray-500 text-sm">Daily, weekly, monthly, or custom patterns</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <MdCheck className="text-green-400" size={14} />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">Smart Skipping</h4>
                        <p className="text-gray-500 text-sm">Skip occurrences without breaking the pattern</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <MdCheck className="text-green-400" size={14} />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">Habit Tracking</h4>
                        <p className="text-gray-500 text-sm">Build streaks and see your consistency over time</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activePersona === 'individuals' && (
                <div>
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">
                    Mood & Energy Insights
                  </h3>
                  <p className="text-lg text-gray-500 mb-6">
                    Track how you feel and understand your energy patterns. Make better decisions about when to tackle challenging tasks.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <MdCheck className="text-green-400" size={14} />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">Pattern Recognition</h4>
                        <p className="text-gray-500 text-sm">Discover when you're most productive</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <MdCheck className="text-green-400" size={14} />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">Task Matching</h4>
                        <p className="text-gray-500 text-sm">Get task suggestions based on your current energy</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <MdCheck className="text-green-400" size={14} />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">Wellbeing Focus</h4>
                        <p className="text-gray-500 text-sm">Balance productivity with mental health</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          </div>
        </div>
        
        {/* Gradient transition to next section */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-b from-transparent to-gray-50 pointer-events-none"></div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 px-6 bg-gradient-to-br from-gray-50 via-white to-amber-50/40 relative overflow-hidden">
        {/* Smooth gradient overlay from top */}
        <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-gray-50 to-transparent pointer-events-none"></div>
        
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 right-10 w-96 h-96 bg-amber-200/30 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 left-10 w-80 h-80 bg-orange-200/30 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-6xl font-bold mb-6 text-gray-900">
              Everything you need to stay organized
            </h2>
            <p className="text-xl md:text-2xl text-gray-500">
              Powerful features designed for modern teams
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {/* Card 1: Large Feature (Spans 2 columns and 2 rows on large screens) */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="group bg-white rounded-[2.5rem] p-8 lg:p-10 hover:shadow-[0_24px_64px_-12px_rgba(0,0,0,0.08)] hover:-translate-y-1 transition-all duration-300 border border-gray-100 lg:col-span-2 lg:row-span-2 flex flex-col justify-between"
            >
              <div>
                <div className="w-16 h-16 bg-[#e8f0fe] text-[#1a73e8] rounded-[1.5rem] flex items-center justify-center mb-8 group-hover:scale-110 transition-transform duration-300">
                  <MdBolt className="text-[32px]" />
                </div>
                <h3 className="text-3xl font-bold mb-4 text-gray-900 leading-tight">Lightning Fast & Intuitive</h3>
                <p className="text-gray-500 text-lg leading-relaxed max-w-md">Create and manage tasks in seconds with our streamlined interface. We've optimized every interaction so you spend less time managing and more time doing.</p>
              </div>
              <div className="mt-8 h-32 bg-[#f8f9fa] rounded-2xl border border-gray-100 flex items-center justify-center overflow-hidden relative">
                 <div className="absolute top-4 left-4 right-4 h-12 bg-white rounded-xl shadow-sm flex items-center px-4 gap-3 opacity-60">
                   <div className="w-4 h-4 rounded border-2 border-gray-300"></div>
                   <div className="w-32 h-2.5 bg-gray-200 rounded-full"></div>
                 </div>
                 <div className="absolute top-20 left-4 right-4 h-12 bg-white rounded-xl shadow-sm flex items-center px-4 gap-3">
                   <div className="w-4 h-4 rounded border-2 border-[#1a73e8] bg-[#1a73e8] flex items-center justify-center"><MdCheck size={10} className="text-white" /></div>
                   <div className="w-48 h-2.5 bg-gray-900 rounded-full text-gray-900 line-through opacity-40"></div>
                 </div>
              </div>
            </motion.div>

            {/* Card 2: Standard */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="group bg-white rounded-[2.5rem] p-8 hover:shadow-[0_24px_64px_-12px_rgba(0,0,0,0.08)] hover:-translate-y-1 transition-all duration-300 border border-gray-100 col-span-1 flex flex-col"
            >
              <div className="w-14 h-14 bg-[#f3e8fd] text-[#9334e6] rounded-[1.25rem] flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <MdGroup className="text-[28px]" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-900">Collaboration</h3>
              <p className="text-gray-500 text-[15px] leading-relaxed flex-1">Assign tasks, share progress, and work together seamlessly.</p>
            </motion.div>

            {/* Card 3: Standard */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="group bg-white rounded-[2.5rem] p-8 hover:shadow-[0_24px_64px_-12px_rgba(0,0,0,0.08)] hover:-translate-y-1 transition-all duration-300 border border-gray-100 col-span-1 flex flex-col"
            >
              <div className="w-14 h-14 bg-[#e6f4ea] text-[#1e8e3e] rounded-[1.25rem] flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <MdTrackChanges className="text-[28px]" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-900">Track Progress</h3>
              <p className="text-gray-500 text-[15px] leading-relaxed flex-1">Monitor your team's progress with visual dashboards.</p>
            </motion.div>

            {/* Card 4: Standard */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="group bg-white rounded-[2.5rem] p-8 hover:shadow-[0_24px_64px_-12px_rgba(0,0,0,0.08)] hover:-translate-y-1 transition-all duration-300 border border-gray-100 col-span-1 flex flex-col"
            >
              <div className="w-14 h-14 bg-[#fef7e0] text-[#f9ab00] rounded-[1.25rem] flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <MdAccessTime className="text-[28px]" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-900">Time Management</h3>
              <p className="text-gray-500 text-[15px] leading-relaxed flex-1">Set deadlines, reminders, and prioritize what matters most.</p>
            </motion.div>

            {/* Card 5: Standard */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="group bg-white rounded-[2.5rem] p-8 hover:shadow-[0_24px_64px_-12px_rgba(0,0,0,0.08)] hover:-translate-y-1 transition-all duration-300 border border-gray-100 col-span-1 flex flex-col"
            >
              <div className="w-14 h-14 bg-[#fce8e6] text-[#d93025] rounded-[1.25rem] flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <MdSecurity className="text-[28px]" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-900">Enterprise Secure</h3>
              <p className="text-gray-500 text-[15px] leading-relaxed flex-1">Your data is encrypted with enterprise-grade security.</p>
            </motion.div>

            {/* Card 6: Standard */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.5 }}
              className="group bg-white rounded-[2.5rem] p-8 hover:shadow-xl transition-all duration-300 border border-gray-100 col-span-1 flex flex-col"
            >
              <div className="w-14 h-14 bg-[#e8eaed] text-[#5f6368] rounded-[1.25rem] flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <MdCloudSync className="text-[28px]" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-900">Cloud Sync</h3>
              <p className="text-gray-500 text-[15px] leading-relaxed flex-1">Access your tasks from anywhere, on any device, in real-time.</p>
            </motion.div>
          </div>
        </div>
        
        {/* Gradient transition to next section */}
        <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-b from-transparent to-white pointer-events-none"></div>
      </section>

      {/* Minimalist FAQ Section */}
      <section className="py-32 px-6 bg-[#f8f9fa] text-[#202124]">
        <div className="max-w-[800px] mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-[40px] md:text-[56px] font-bold mb-6 tracking-tight text-[#202124]">
              Frequently asked questions
            </h2>
          </div>
          
          <div className="space-y-2">
            {[
              { q: 'Is there a free trial available?', a: 'Yes, we offer a completely free 14-day trial for all new users. No credit card required.' },
              { q: 'Can I invite my team later?', a: 'Absolutely. You can start as an individual and upgrade to a team plan at any point. Your data will seamlessly migrate.' },
              { q: 'How secure is my data?', a: 'We use enterprise-grade end-to-end encryption. Your data is stored on secure Google Cloud servers with daily backups.' },
              { q: 'Do you offer an API?', a: 'Yes, our Enterprise plan includes full REST API access, allowing you to integrate Snappy with your internal tools.' }
            ].map((faq, index) => (
              <div key={index} className="border-b border-gray-200">
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full py-8 flex justify-between items-center text-left hover:text-[#1a73e8] transition-colors"
                >
                  <span className="text-[24px] font-medium leading-tight pr-8">{faq.q}</span>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-transform duration-300 ${openFaq === index ? 'bg-[#e8f0fe] text-[#1a73e8] rotate-180' : 'bg-gray-100 text-gray-500'}`}>
                    {openFaq === index ? <MdRemove size={20} /> : <MdAdd size={20} />}
                  </div>
                </button>
                <AnimatePresence>
                  {openFaq === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden"
                    >
                      <p className="pb-8 text-[#5f6368] text-lg leading-relaxed">{faq.a}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-40 px-6 bg-white text-[#202124] relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#e8f0fe] rounded-full blur-[100px] opacity-60"></div>
        </div>
        
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-[48px] md:text-[64px] font-bold mb-6 tracking-tight">
              Ready to get <span className="text-[#1a73e8]">organized</span>?
            </h2>
            <p className="text-xl text-gray-400 mb-10">
              Join thousands of teams already using Snappy to manage their daily tasks
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link
                to="/register"
                className="inline-flex items-center gap-2 px-10 py-4 bg-[#1a73e8] text-white rounded-full font-medium text-[16px] hover:bg-[#1557b0] transition-all shadow-[0_4px_14px_rgba(26,115,232,0.3)] active:scale-95"
              >
                Get Started Free <MdArrowForward />
              </Link>
              <Link
                to="/register"
                className="inline-flex items-center gap-2 px-10 py-4 bg-white text-[#202124] rounded-full font-medium text-[16px] hover:bg-gray-50 transition-all border border-gray-200 shadow-sm active:scale-95"
              >
                View Demo
              </Link>
            </div>
            <p className="text-sm text-gray-500 mt-8">
              ⭐ No credit card required • Free forever • Setup in 2 minutes
            </p>
          </motion.div>
        </div>
        
        {/* Gradient transition to footer */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-b from-transparent to-white pointer-events-none"></div>
      </section>

      {/* Footer */}
      <footer className="py-16 px-6 bg-gray-50 border-t border-gray-200">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-12">
            <div>
              <div className="flex items-center gap-1 mb-4">
                <svg width="32" height="32" viewBox="0 0 32 32" fill="none" className="text-gray-900 mt-[0.35rem]">
                  <path d="M12 8L8 12L12 16M20 8L24 12L20 16M16 4L14 20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span className="text-xl font-bold text-gray-900">Snappy</span>
              </div>
              <p className="text-gray-400 text-sm mb-4">
                Your daily tasks organized effortlessly.
              </p>
              <div className="flex gap-4">
                <a href="#" className="text-gray-500 hover:text-gray-900 transition-colors">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z"/></svg>
                </a>
                <a href="#" className="text-gray-500 hover:text-gray-900 transition-colors">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/></svg>
                </a>
                <a href="#" className="text-gray-500 hover:text-gray-900 transition-colors">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/></svg>
                </a>
              </div>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-4">Product</h4>
              <ul className="space-y-3">
                <li><a href="#features" className="text-gray-500 hover:text-gray-900 text-sm transition-colors">Features</a></li>
                <li><a href="#pricing" className="text-gray-500 hover:text-gray-900 text-sm transition-colors">Pricing</a></li>
                <li><a href="#" className="text-gray-500 hover:text-gray-900 text-sm transition-colors">Security</a></li>
                <li><a href="#" className="text-gray-500 hover:text-gray-900 text-sm transition-colors">Roadmap</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-4">Company</h4>
              <ul className="space-y-3">
                <li><a href="#" className="text-gray-500 hover:text-gray-900 text-sm transition-colors">About</a></li>
                <li><a href="#" className="text-gray-500 hover:text-gray-900 text-sm transition-colors">Blog</a></li>
                <li><a href="#" className="text-gray-500 hover:text-gray-900 text-sm transition-colors">Careers</a></li>
                <li><a href="#" className="text-gray-500 hover:text-gray-900 text-sm transition-colors">Contact</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-4">Legal</h4>
              <ul className="space-y-3">
                <li><a href="#" className="text-gray-500 hover:text-gray-900 text-sm transition-colors">Privacy</a></li>
                <li><a href="#" className="text-gray-500 hover:text-gray-900 text-sm transition-colors">Terms</a></li>
                <li><a href="#" className="text-gray-500 hover:text-gray-900 text-sm transition-colors">Cookie Policy</a></li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t border-gray-200 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-gray-500">
              © 2025 Snappy. All rights reserved.
            </p>
            <div className="flex gap-6 text-sm text-gray-500">
              <a href="#" className="hover:text-gray-900 transition-colors">Status</a>
              <a href="#" className="hover:text-gray-900 transition-colors">Documentation</a>
              <a href="#" className="hover:text-gray-900 transition-colors">Support</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
